import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {observer} from 'mobx-react';
import {Button, SliderPanel, PageContainer, Tooltip, Breadcrumbs, CircleLoader, Surface, Modal, ConfirmPopMessage} from '@vidazoo/ui-kit';
import theme from '../../../../../common/styles/base.module.scss';
import UrlsService from '../../../../../common/router/services/urlsService.mdl';
import {ChartContext} from '../../context/ChartContext';
import * as React from 'react';
import useUserManager from '../../../../../common/hooks/useUserManager';
import useEditChartsManager from '../../../../../common/hooks/useEditChartsManager';
import ChartDetails from '../../components/chartDetails/ChartDetails';
import {FormMode} from '../../../../../common/enums/FormMode';
import ChartDisplay from '../../../../../common/components/chart-display/ChartDisplay';
import styles from './theme.module.scss';
import useReportingMetaDataManager from '../../../../../common/hooks/useReportingMetaDataManager';

export default observer(function ChartEdit() {
    const userManager = useUserManager();
    const {id} = useParams<{ id: string; }>();
    const editChartsManager = useEditChartsManager();
    const metadata = useReportingMetaDataManager();
    const [showDeleteMessage, setShowDeleteMessage] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const model = editChartsManager.getModel(id);

    useEffect(() => {
        editChartsManager.initChartModel(id);
    }, [id, editChartsManager]);

    useEffect(() => {
        if (model && !model.item.isLoading && !model.item.error) model.getReport();
    }, [model, model?.item.isLoading, metadata.timePreset]);

    const contextValue = useMemo(() => ({model}), [model]);
    const navigate = useNavigate();

    if (!model || model.item.isLoading) {
        return (
            <div className={theme.center}>
                <CircleLoader size={4} gray/>
            </div>
        );
    }

    if (model.item.error) return <PageContainer inner top={1.2}>
        <div className={theme.message} role="alert">
            <h2>Failed to load chart</h2>
            <p>We had an error loading the requested chart.</p>
            <Button type="button" onClick={() => model.item.invalidate().fetch(id)}>Retry</Button>
        </div>
    </PageContainer>;

    const isBelongToUser = userManager.isBelongToUser(model.item.data?.user?._id || (model.item.data?.user) as any);

    const breadcrumbs = [{
        label: "Charts",
        iconName: "icon-reporting",
        link: {to: UrlsService.charts()}
    }, {
        label: "Update"
    }, {
        label: model.getTitle(),
        primary: true
    }];

    const onCancelClick = () => {
        navigate(UrlsService.charts());
    };

    return (
        <ChartContext.Provider value={contextValue}>
            <PageContainer className={styles.page} pageTitle={['Edit Chart', model.getTitle()]} inner top={1.2}>
                <Breadcrumbs className={theme.breadcrumbs} breadcrumbs={breadcrumbs}/>
                <form ref={formRef} className={`${theme.form} ${styles.form}`} onSubmit={event => {
                    event.preventDefault();
                    if (isBelongToUser) model.submit();
                }}>
                    <ChartDetails mode={FormMode.Edit} className={styles.details}/>
                    <Surface className={styles.preview}>
                        <ChartDisplay appearance="classic" chartModel={model} isEditMode={false}/>
                    </Surface>
                    <SliderPanel>
                        <div className={theme.sliderPanel}>
                            <Button type="button" medium gray uppercase onClick={onCancelClick}>Cancel</Button>
                            <Button type="button" medium red uppercase onClick={() => setShowDeleteMessage(true)}>Delete</Button>
                            <Button type="button" medium gray60 uppercase onClick={() => model.duplicate()}>Duplicate</Button>
                            <Tooltip disabled={isBelongToUser} title="You can not update item that you not create">
                                <div>
                                    <Button type="button" disabled={!isBelongToUser} medium blue uppercase
                                            onClick={() => formRef.current?.requestSubmit()}>Update</Button>
                                </div>
                            </Tooltip>
                        </div>
                    </SliderPanel>
                </form>
                <Modal external exitOnClickOutside onClose={() => setShowDeleteMessage(false)} isOpen={showDeleteMessage}>
                    <ConfirmPopMessage red title="Delete Chart?" text={`Are you sure you want to delete ${model.item.data.name}?`}
                        cancelText="No, Cancel" confirmText="Yes, Delete" onCancelClick={() => setShowDeleteMessage(false)}
                        onConfirmClick={() => {setShowDeleteMessage(false); model.delete();}}/>
                </Modal>
            </PageContainer>
        </ChartContext.Provider>
    );
});
